import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useConstructionOrder, useUpdateConstructionOrder } from '@saiens/shared/hooks';
import { formatDate, getConstructionStateLabel } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { LineItemSection } from './components/LineItemSection';
import { PhotoSection } from './components/PhotoSection';
import {
  ArrowLeft,
  Calendar,
  User,
  Users,
  Truck,
  Building,
  MapPin,
  FileText,
  Edit,
  Square,
  Package,
  Wrench,
  MoreHorizontal,
  Send,
  CheckCircle,
  X,
} from 'lucide-react';
import type { ConstructionState } from '@saiens/shared/types';

const stateFlow: ConstructionState[] = [
  'install',
  'in_one',
  'in_two',
  'in_three',
  'beauty',
  'repair',
  'finish',
];

function getNextState(current: string): ConstructionState | null {
  const idx = stateFlow.indexOf(current as ConstructionState);
  if (idx === -1 || idx === stateFlow.length - 1) return null;
  return stateFlow[idx + 1];
}

function getStateColor(state: string): string {
  const colors: Record<string, string> = {
    install: 'bg-blue-100 text-blue-700',
    in_one: 'bg-yellow-100 text-yellow-700',
    in_two: 'bg-yellow-100 text-yellow-700',
    in_three: 'bg-yellow-100 text-yellow-700',
    beauty: 'bg-pink-100 text-pink-700',
    repair: 'bg-orange-100 text-orange-700',
    finish: 'bg-green-100 text-green-700',
  };
  return colors[state] || 'bg-gray-100 text-gray-700';
}

export function ConstructionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const orderId = id ? parseInt(id, 10) : undefined;

  const { data: order, isLoading, error } = useConstructionOrder(orderId);
  const updateOrder = useUpdateConstructionOrder();

  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [memoValue, setMemoValue] = useState('');

  const handleEditMemo = () => {
    setMemoValue(order?.memo || '');
    setIsEditingMemo(true);
  };

  const handleSaveMemo = () => {
    if (!orderId) return;
    updateOrder.mutate(
      { orderId, data: { memo: memoValue } },
      {
        onSuccess: () => {
          setIsEditingMemo(false);
        },
        onError: () => {
          // Keep edit mode open so user doesn't lose their changes
          // Toast error is shown by the hook
        },
      }
    );
  };

  const handleCancelEditMemo = () => {
    setIsEditingMemo(false);
    setMemoValue('');
  };

  const handleAdvanceState = () => {
    if (!orderId || !order) return;
    const nextState = getNextState(order.state);
    if (!nextState) return;

    updateOrder.mutate({ orderId, data: { state: nextState } });
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="px-4 py-6">
        <Link to="/construction" className="flex items-center gap-1 text-primary">
          <ArrowLeft className="h-4 w-4" /> 返回列表
        </Link>
        <div className="mt-8 text-center text-red-500">載入失敗或施工單不存在</div>
      </div>
    );
  }

  const nextState = getNextState(order.state);
  const saleOrder = order.sale_order;

  return (
    <div className="pb-32 px-4 py-6">
      {/* Back button */}
      <Link to="/construction" className="mb-4 flex items-center gap-1 text-sm text-primary">
        <ArrowLeft className="h-4 w-4" /> 返回列表
      </Link>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">{order.name}</h1>
        <div className="mt-2 flex items-center gap-2">
          <span className={`inline-block rounded-full px-3 py-1 text-sm ${getStateColor(order.state)}`}>
            {getConstructionStateLabel(order.state)}
          </span>
          {order.sale_order_name && (
            <span className="text-sm text-gray-500">{order.sale_order_name}</span>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="space-y-4">
        {/* Basic Info */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">基本資訊</h2>
          <div className="space-y-2">
            <InfoRow
              icon={<Calendar />}
              label="施作日期"
              value={formatDate(order.date_order)}
            />
            {saleOrder?.project_name && (
              <InfoRow
                icon={<FileText />}
                label="案件名稱"
                value={saleOrder.project_name}
              />
            )}
            {saleOrder?.project_addr && (
              <InfoRow
                icon={<MapPin />}
                label="專案地址"
                value={saleOrder.project_addr}
              />
            )}
            {order.lead_user_name && (
              <InfoRow
                icon={<User />}
                label="工頭"
                value={order.lead_user_name}
              />
            )}
            {order.moving_trip !== undefined && order.moving_trip > 0 && (
              <InfoRow
                icon={<Truck />}
                label="搬運趟數"
                value={`${order.moving_trip} 趟`}
              />
            )}
            {order.moving_floor !== undefined && order.moving_floor > 0 && (
              <InfoRow
                icon={<Building />}
                label="樓層"
                value={`${order.moving_floor}F`}
              />
            )}
          </div>
        </div>

        {/* Team */}
        {order.construct_team && order.construct_team.length > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-900">
              <Users className="h-4 w-4" />
              安裝單隊
            </h2>
            <div className="flex flex-wrap gap-2">
              {order.construct_team.map((member) => (
                <span
                  key={member.id}
                  className="inline-block rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-700"
                >
                  {member.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Line Items Sections */}
        <LineItemSection
          title="檯面項目"
          icon={<Square />}
          items={order.order_line_one || []}
          defaultExpanded={true}
        />

        <LineItemSection
          title="材料項目"
          icon={<Package />}
          items={order.order_line_two || []}
        />

        <LineItemSection
          title="加工項目"
          icon={<Wrench />}
          items={order.order_line_three || []}
        />

        <LineItemSection
          title="其他項目"
          icon={<MoreHorizontal />}
          items={order.order_line_four || []}
        />

        {/* Construction Extras */}
        {order.construction_extras && order.construction_extras.length > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">工地狀況</h2>
            <ul className="space-y-2">
              {order.construction_extras.map((extra) => (
                <li key={extra.id} className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  <span>
                    {extra.extra_name}
                    {extra.quantity > 1 && ` x${extra.quantity}`}
                    {extra.extra_text && `: ${extra.extra_text}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Photo Section */}
        {orderId && <PhotoSection orderId={orderId} />}

        {/* Memo */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">備註</h2>
            {!isEditingMemo && (
              <button
                onClick={handleEditMemo}
                className="flex items-center gap-1 text-sm text-primary"
              >
                <Edit className="h-4 w-4" />
                編輯
              </button>
            )}
          </div>

          {isEditingMemo ? (
            <div className="space-y-3">
              <textarea
                value={memoValue}
                onChange={(e) => setMemoValue(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-primary focus:outline-none"
                placeholder="輸入備註..."
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleCancelEditMemo}
                  disabled={updateOrder.isPending}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                >
                  <X className="h-4 w-4" />
                  取消
                </button>
                <button
                  onClick={handleSaveMemo}
                  disabled={updateOrder.isPending}
                  className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 disabled:bg-gray-300"
                >
                  {updateOrder.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      儲存
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-gray-600">
              {order.memo || '無備註'}
            </p>
          )}
        </div>
      </div>

      {/* Action Button - Fixed at bottom */}
      {nextState && (
        <div className="fixed bottom-20 left-0 right-0 z-40 border-t bg-white p-4">
          <button
            onClick={handleAdvanceState}
            disabled={updateOrder.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
          >
            {updateOrder.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Send className="h-4 w-4" />
                推進至 {getConstructionStateLabel(nextState)}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2 text-gray-400">
        <span className="h-4 w-4">{icon}</span>
        <span className="text-sm">{label}</span>
      </div>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}
